import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  Link,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  fetchTestPlanWorkItem,
  generateCiTestPlan,
  getCiRuns,
  getCiTestPlanUrl,
} from '../../services/apiService';

const POLL_INTERVAL_MS = 30000; // poll the table every 30s

const STATUS_CHIP = {
  running: { label: 'Running', color: '#b26a00', bg: '#fff3e0' },
  completed: { label: 'Completed', color: '#2e7d32', bg: '#e8f5e9' },
  failed: { label: 'Failed', color: '#c62828', bg: '#ffebee' },
};

/**
 * Test Plans page (persistent, table-based).
 *  - Enter an ADO id + Generate → a row is inserted (status "Running") and the
 *    Test Planning workflow is dispatched.
 *  - The table polls every 30s; the server reconciles running rows against their
 *    GitHub run and flips them to Completed (with a Test Plan link) or Failed.
 *  - State is persisted server-side, so navigating away / refreshing is fine.
 */
const TestPlanGenerator = () => {
  const [adoId, setAdoId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Per-row selected version for the "View" link (defaults to latest).
  const [selectedVersions, setSelectedVersions] = useState({});

  const pollRef = useRef(null);

  const loadRows = useCallback(async () => {
    try {
      const resp = await getCiRuns();
      if (resp && resp.success && Array.isArray(resp.data)) {
        setRows(resp.data);
      }
    } catch (err) {
      console.error('Error loading test plan runs:', err);
    }
  }, []);

  // Initial load + 30s polling (independent of any single generation).
  useEffect(() => {
    setLoading(true);
    loadRows().finally(() => setLoading(false));
    pollRef.current = setInterval(loadRows, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadRows]);

  const handleGenerate = async () => {
    const id = adoId.trim();
    if (!/^\d+$/.test(id)) {
      setError('Enter a numeric ADO ticket ID.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Capture work-item details (best effort) so the table row is populated.
      let details = {};
      try {
        const wiResp = await fetchTestPlanWorkItem(id);
        if (wiResp && wiResp.success && wiResp.data) {
          const wi = wiResp.data.workItem || {};
          details = {
            title: wi.title || '',
            workItemType: wi.workItemType || '',
            state: wi.state || '',
            team: wi.team || '',
            descendantsCount: Array.isArray(wiResp.data.descendants) ? wiResp.data.descendants.length : 0,
          };
        }
      } catch (_) { /* not in DB — generate with minimal details */ }

      const resp = await generateCiTestPlan(id, details);
      if (resp && resp.success) {
        setAdoId('');
        await loadRows();
      } else {
        setError(resp?.message || 'Failed to start generation.');
      }
    } catch (err) {
      setError(err.message || 'Failed to start generation.');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => { if (e.key === 'Enter') handleGenerate(); };

  const openPlan = (row) => {
    const version = selectedVersions[row.adoId] || row.latestVersion;
    if (!version) return;
    window.open(getCiTestPlanUrl(row.adoId, version), '_blank', 'noopener,noreferrer');
  };

  const renderTestPlanCell = (row) => {
    if (row.status === 'completed' && row.latestVersion) {
      const selected = selectedVersions[row.adoId] || row.latestVersion;
      const options = Array.from({ length: row.latestVersion }, (_, i) => row.latestVersion - i); // latest first
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {row.latestVersion > 1 && (
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select
                value={selected}
                onChange={(e) => setSelectedVersions((p) => ({ ...p, [row.adoId]: e.target.value }))}
              >
                {options.map((v) => <MenuItem key={v} value={v}>v{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />} onClick={() => openPlan(row)}>
            {row.latestVersion > 1 ? 'View' : `View v${row.latestVersion}`}
          </Button>
        </Box>
      );
    }
    if (row.status === 'failed') {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ color: '#c62828' }}>—</Typography>
          {row.runUrl && (
            <Link href={row.runUrl} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              Open run ↗
            </Link>
          )}
        </Box>
      );
    }
    // running — link to the in-progress GitHub Actions run if we have it yet.
    if (row.runUrl) {
      return (
        <Link href={row.runUrl} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          View run ↗
        </Link>
      );
    }
    return <Typography variant="body2" sx={{ color: '#999' }}>—</Typography>;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Test Plans</Typography>
      <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
        Enter an ADO ticket ID to generate an impacted-test-scenarios plan. Generation runs in the
        background — you can navigate away; the table updates automatically.
      </Typography>

      {/* Input */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e0e0e0', mb: 3 }}>
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            label="ADO Ticket ID"
            value={adoId}
            onChange={(e) => setAdoId(e.target.value.replace(/[^0-9]/g, ''))}
            onKeyDown={onKeyDown}
            size="small"
            sx={{ width: 220 }}
            placeholder="e.g. 904578"
          />
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleGenerate}
            disabled={submitting || !adoId.trim()}
          >
            Generate Test Plan
          </Button>
          <Tooltip title="Refresh now">
            <span>
              <Button variant="text" startIcon={<RefreshIcon />} onClick={loadRows}>Refresh</Button>
            </span>
          </Tooltip>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>ADO ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Team</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Descendants</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Test Plan Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Test Plan</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant="body2" sx={{ color: '#999', py: 2 }}>
                      No test plans yet. Enter an ADO ticket ID above and click Generate.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const chip = STATUS_CHIP[row.status] || STATUS_CHIP.running;
                return (
                  <TableRow key={row.adoId} hover>
                    <TableCell sx={{ fontWeight: 600 }}>#{row.adoId}</TableCell>
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {row.title || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.workItemType || '—'}</TableCell>
                    <TableCell>{row.state || '—'}</TableCell>
                    <TableCell>{row.team || '—'}</TableCell>
                    <TableCell align="center">{row.descendantsCount ?? 0}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={chip.label}
                          size="small"
                          icon={row.status === 'running' ? <CircularProgress size={12} thickness={6} sx={{ color: chip.color }} /> : undefined}
                          sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600 }}
                        />
                        {row.status === 'failed' && row.error && (
                          <Tooltip title={row.error}><Typography variant="caption" sx={{ color: '#c62828', cursor: 'help' }}>details</Typography></Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{renderTestPlanCell(row)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TestPlanGenerator;
